import React, { Fragment, useState } from 'react';
import { withRouter } from 'react-router';
import { TruncatedText } from '@/se/components/typography';
import { formatPhoneNumber, processPhoneNumbersString } from '@/se/components/inputs/PhoneInput';
import RouterLink from '@/se/components/RouterLink';
import { sortNumber, sortString } from '@/util/sort';
import { Check } from '@material-ui/icons';
import { PinColumn } from '../common/columns';
import { gql, useMutation } from '@apollo/client';
import { fragments, list, removeAndAssign } from '@/graph/physicians';
import { formatProviderId } from '@/util/providerId';
import TrackingColumn from '../../tracking/TrackingColumn';
import LinkButton from '@/se/components/LinkButton';
import Typography from '@material-ui/core/Typography';
import Button from '@/se/components/Button[DEPRECATED]';
import Modal from '@/se/components/Modal';
import { withProps } from 'recompose';
import graphqlSelectInput from '@/components/inputs/graphqlSelectInput';
import { Box } from '@material-ui/core';
import { useToaster } from '@/components/core/Toaster';
import MessageHistorySidePanel from '../common/MessageHistorySidePanel';

export const nameColumn = {
  title: 'Name',
  lens: item => item.name,
  sortFunction: (l, r) => sortString(l.name, r.name),
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

export const trackingColumn = {
  title: 'Location',
  lens: data => ({ bracelet: data.bracelet, room: data.room }),
  Component: ({ data: { bracelet, room } }) => <TrackingColumn bracelet={bracelet} room={room} />,
};

export const emailColumn = {
  title: 'Email',
  lens: item => item.email,
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

export const hstIdColumn = {
  title: 'Provider Id',
  lens: item => item.hstId,
  sortFunction: (l, r) => sortNumber(l.hstId, r.hstId),
  Component: props => <TruncatedText>{formatProviderId(props.data)}</TruncatedText>,
};

export const smsNotificationColumn = {
  title: 'SMS Notification Numbers',
  lens: data =>
    data.notificationNumbers !== null && data.notificationNumbers !== undefined
      ? formatPhoneNumber(processPhoneNumbersString(data.notificationNumbers).nationalNumbers)
      : undefined,
  Component: props => <TruncatedText>{props.data || '-'}</TruncatedText>,
};

// Phone column with history icon for single entity views
const PhysicianPhoneWithHistory = ({ data }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between">
    <TruncatedText>{data.formatted || '-'}</TruncatedText>
    {data.raw && <MessageHistorySidePanel phone={data.raw} label={`${data.name || 'Physician'} SMS History`} />}
  </Box>
);

export const smsNotificationColumnWithHistory = {
  title: 'SMS Notification Numbers',
  lens: data => ({
    formatted:
      data.notificationNumbers !== null && data.notificationNumbers !== undefined
        ? formatPhoneNumber(processPhoneNumbersString(data.notificationNumbers).nationalNumbers)
        : undefined,
    raw: data.notificationNumbers,
    name: data.name,
  }),
  Component: PhysicianPhoneWithHistory,
};

export const scheduleAccessColumn = {
  title: 'Schedule Access',
  lens: data => data,
  Component: withRouter(props => (
    <RouterLink onClick={e => e.stopPropagation()} to={`${props.match.url}/schedule-access/${props.data.id}`}>
      {props.data.hasScheduleAccess ? <Check fontSize={'small'} style={{ marginRight: '.25em' }} /> : null}
      <TruncatedText>{props.data.hasScheduleAccess ? 'Yes' : 'Manage'}</TruncatedText>
    </RouterLink>
  )),
};

export const sendNotificationColumn = {
  title: 'Notify',
  lens: item => item.shouldSendNotification,
  Component: props => <TruncatedText>{props.data ? 'Yes' : 'No'}</TruncatedText>,
};

export const excludeFromPreopRegistrationColumn = {
  title: 'Exclude from Pre-op',
  lens: item => item.excludeFromPreopRegistration,
  Component: props => <TruncatedText>{props.data ? 'Yes' : 'No'}</TruncatedText>,
};

export const specialityColumn = {
  title: 'Speciality',
  lens: item => item.speciality,
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

export const categoryColumn = {
  title: 'Category',
  lens: item => item.category,
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

export const pinColumn = PinColumn({
  getId: data => data.id,
  getName: data => data.name,
  getPin: data => data.pin,
  createPinMutation: gql`
    mutation physicianCreatePin($id: Long!, $pin: String!) {
      physicianSetPin(id: $id, pin: $pin) {
        ...PhysicianData
        pin
      }
    }
    ${fragments.all}
  `,
  issuePinMutation: gql`
    mutation physicianIssuePin($id: Long!) {
      physicianIssuePin(id: $id) {
        ...PhysicianData
        pin
      }
    }
    ${fragments.all}
  `,
  removePinMutation: gql`
    mutation physicianRemovePin($id: Long!) {
      physicianRemovePin(id: $id) {
        ...PhysicianData
        pin
      }
    }
    ${fragments.all}
  `,
});

const PhysicianSelectInput = withProps({
  isClearable: false,
  useLegacy: true,
})(
  graphqlSelectInput({
    entityName: 'Physician',
    placeholder: 'e.g., Mark Nordstrom',
    graphqlList: list,
    getOptionValue: option => (option ? parseInt(option.value, 10) : undefined),
    sortOptions: true,
  })
);

const removeAndAssignColumn = {
  title: 'Actions',
  lens: item => item,
  Component: withRouter(props => {
    const [mutate, { loading }] = useMutation(removeAndAssign, {
      variables: {
        id: props.data.id,
      },
      refetchQueries: [{ query: list }],
    });

    const [open, setOpen] = useState(false);

    const [physician, setPhysician] = useState(null);

    const toaster = useToaster();

    const handleSubmit = async () => {
      try {
        await mutate({ variables: { id: props.data.id, assignTo: physician } });
        setOpen(false);
        props.history.goBack();
        toaster.success('Removed ' + props.data.name);
      } catch (e) {
        toaster.error('Unable to remove and assign physician: ' + e.message);
      }
    };

    return (
      <>
        <LinkButton type="button" danger onClick={() => setOpen(true)} busy={loading}>
          Remove and assign
        </LinkButton>
        {open && (
          <Modal title="Remove and assign" withRouter={false} onClose={() => setOpen(false)}>
            <Typography variant="h6" gutterBottom>
              {props.data.name}
            </Typography>
            <Typography variant="body2">Select physician to assign all the procedures and references to.</Typography>
            <Box mt={2}>
              <PhysicianSelectInput label="Assign to" value={physician} onChange={setPhysician} />
            </Box>
            <div style={{ marginTop: '2rem' }}>
              <Button
                disabled={loading || !physician || physician === props.data.id}
                danger
                style={{ marginRight: '1rem' }}
                onClick={handleSubmit}
              >
                Remove and assign
              </Button>
              <Button disabled={loading} onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
          </Modal>
        )}
      </>
    );
  }),
};

export const baseColumns = [
  nameColumn,
  hstIdColumn,
  smsNotificationColumn,
  scheduleAccessColumn,
  specialityColumn,
  sendNotificationColumn,
  categoryColumn,
  pinColumn,
  trackingColumn,
];

export const listColumns = [...baseColumns];

export const viewColumns = [
  specialityColumn,
  hstIdColumn,
  sendNotificationColumn,
  excludeFromPreopRegistrationColumn,
  smsNotificationColumnWithHistory,
  emailColumn,
  categoryColumn,
  trackingColumn,
  removeAndAssignColumn,
];
